import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AutomationInterface, Rule, WorkflowStepOutputParameterContainer } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TRIGGER_STEP_ID } from '../../../../../common/constants';
import { IfElseBranchWorkflowStepTaskDefinitionId } from '../../../../component-loader/constants';
import { AutomationErrorHandlerService } from '../../../../services';
import {
  extractWorkflowStepIdFromReplacementTag,
  extractWorkflowStepParameterKeyFromReplacementTag,
} from '../../../common/utils';
import { DataFilter, DataFilterDisplayService, FilterType } from '../../definitions';
import { FilterDefinitionService } from '../../definitions/filter-definition.service';

// Because we did a lazy migration of the waitForTriggerFilters if no step can be extracted from the value then we
// can assume it's looking at stream data which comes from the trigger so the step is the trigger.
export function extractWorkflowStepIdFromWaitForTriggerFilter(waitForTriggerFilterDataValue: string): string {
  let id = TRIGGER_STEP_ID;
  if (waitForTriggerFilterDataValue) {
    const extractedId = extractWorkflowStepIdFromReplacementTag(waitForTriggerFilterDataValue);
    id = extractedId ? extractedId : id;
  }
  return id;
}

@Injectable({ providedIn: 'root' })
export class FilterDataService {
  constructor(
    private readonly translate: TranslateService,
    private readonly injector: Injector,
    private readonly filterDefinitions: FilterDefinitionService,
    private readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  private getSelectedDataLabel(dataFilter: DataFilter): string {
    return dataFilter?.label;
  }

  private getSelectedComparisonLabel(dataFilter: DataFilter, comparisonIndex: number): string {
    return dataFilter.comparisonLabels[comparisonIndex];
  }

  private displayHelper(
    currentDataComponent: DataFilter,
    nodeTextKey: string,
    comparisonIndex: number,
    displayValue: string,
  ): string {
    let display: string;

    if (currentDataComponent.subgroup) {
      display = currentDataComponent.subgroup.name;
    } else {
      display = this.translate.instant(nodeTextKey);
    }

    display +=
      ' ' +
      this.translate.instant(this.getSelectedDataLabel(currentDataComponent)).toLowerCase() +
      ' ' +
      this.translate.instant(this.getSelectedComparisonLabel(currentDataComponent, comparisonIndex)).toLowerCase() +
      ' ' +
      displayValue;
    return display;
  }

  getFilter(taskDefinitionId: string, predecessorOutputParams?: WorkflowStepOutputParameterContainer[]): FilterType {
    return this.filterDefinitions.getFilter(taskDefinitionId, predecessorOutputParams);
  }

  ruleDisplay(automation: AutomationInterface, rule: Rule, taskDefinitionId: string, stepId = ''): Observable<string> {
    return this.filterDefinitions.getFilterDataComponentsForDisplay(taskDefinitionId, rule).pipe(
      switchMap((rulesDisplay) => {
        let service: DataFilterDisplayService;
        try {
          service = this.injector.get<DataFilterDisplayService>(rulesDisplay.dataFilter.displayService);
          if (service.initialize) {
            service.initialize(rulesDisplay.dataFilter.filterData);
          }
        } catch (e) {
          console.error(e);
        }
        if (rulesDisplay.dataFilter.validationError && stepId) {
          if ((automation.triggerFilters || []).map((t) => t.id).includes(stepId)) {
            stepId = automation.triggerStep.id;
          }
          this.errorHandler.setValidationError({
            stepId: stepId,
            taskDefinitionId: taskDefinitionId,
            errorMessage: this.translate.instant(rulesDisplay.dataFilter.validationError.panelError),
            isNonDismissable: true,
          });
        }
        let displayValueContainer = service.getDisplay(rule.extractValue());
        if (typeof displayValueContainer === 'string') {
          displayValueContainer = of(displayValueContainer);
        }

        if (taskDefinitionId === IfElseBranchWorkflowStepTaskDefinitionId) {
          return displayValueContainer.pipe(
            map((displayValue) => {
              const workflowStepReplacementTag = rulesDisplay.dataFilter.rules[0].getVariablePath();
              const stepId = extractWorkflowStepIdFromReplacementTag(workflowStepReplacementTag);
              const step = (automation.workflow || []).concat(automation.triggerStep).find((s) => s?.id === stepId);
              const name = step?.name || step?.id;
              return (
                name +
                '[' +
                extractWorkflowStepParameterKeyFromReplacementTag(workflowStepReplacementTag) +
                ']' +
                ' ' +
                this.translate
                  .instant(this.getSelectedComparisonLabel(rulesDisplay.dataFilter, rulesDisplay.comparisonIndex))
                  .toLowerCase() +
                ' ' +
                displayValue
              );
            }),
          );
        }

        return (displayValueContainer as Observable<string>).pipe(
          map((displayValue) => {
            return this.displayHelper(
              rulesDisplay.dataFilter,
              rulesDisplay.nodeTextKey,
              rulesDisplay.comparisonIndex,
              displayValue,
            );
          }),
        );
      }),
    );
  }
}
